/**
 * Garmin Connect APIサービス
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { apiClient } from '@/api/client'
import type {
  GarminActivitiesResponse,
  GarminBodyBatteryResponse,
  GarminDailySummary,
  GarminDashboardResponse,
  GarminHeartRateRaw,
  GarminSleepRaw,
  GarminSteps15Min,
  GarminStepsHourly,
  GarminStepsRaw,
  GarminStressRaw,
  GarminWeeklySummaryResponse,
} from '@/api/types/garmin'

/**
 * 今日のダッシュボードデータを取得
 */
export const getGarminDashboard = async (date?: string): Promise<GarminDashboardResponse> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/dashboard', { params })
  return res.data
}

/**
 * 指定日の健康サマリーを取得
 */
export const getGarminDaily = async (date?: string): Promise<GarminDailySummary> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/daily', { params })
  return res.data
}

/**
 * 過去7日間の健康サマリーを取得
 */
export const getGarminWeekly = async (endDate?: string): Promise<GarminWeeklySummaryResponse> => {
  const params = endDate ? { end_date: endDate } : {}
  const res = await apiClient.get('/garmin/weekly', { params })
  return res.data
}

/**
 * アクティビティ一覧を取得
 */
export const getGarminActivities = async (start = 0, limit = 20): Promise<GarminActivitiesResponse> => {
  const res = await apiClient.get('/garmin/activities', { params: { start, limit } })
  return res.data
}

/**
 * Body Batteryデータを取得
 */
export const getGarminBodyBattery = async (date?: string): Promise<GarminBodyBatteryResponse> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/body-battery', { params })
  return res.data
}

/**
 * 心拍数データを取得（生データ）
 */
export const getGarminHeartRate = async (date?: string): Promise<GarminHeartRateRaw> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/heart-rate', { params })
  return res.data
}

/**
 * 歩数データを取得（15分間隔）
 */
export const getGarminSteps = async (date?: string): Promise<GarminStepsRaw[]> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/steps', { params })
  return res.data
}

/**
 * 1時間単位の歩数データを取得
 */
export const getGarminStepsHourly = async (date?: string): Promise<GarminStepsHourly[]> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/steps/hourly', { params })
  return res.data
}

/**
 * 15分単位の歩数データを取得
 */
export const getGarminSteps15Min = async (date?: string): Promise<GarminSteps15Min[]> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/steps/15min', { params })
  return res.data
}

/**
 * 睡眠データを取得（生データ）
 */
export const getGarminSleep = async (date?: string): Promise<GarminSleepRaw> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/sleep', { params })
  return res.data
}

/**
 * ストレスデータを取得（生データ）
 */
export const getGarminStress = async (date?: string): Promise<GarminStressRaw> => {
  const params = date ? { date } : {}
  const res = await apiClient.get('/garmin/stress', { params })
  return res.data
}
